import React from 'react';
import { usePageLastUpdated } from '../../hooks/usePageLastUpdated';
import './ProjectPage.css';

const PAGE_LAST_UPDATED = { dateTime: '2010-12-29', displayDate: 'December 29, 2010' };

const NavLinks = () => (
  <p className="text-small text-italic text-gray">
    <a href="#top">Go to top</a> | <a href="#contents">Go to contents</a>
  </p>
);

const SimStreamPage = () => {
  usePageLastUpdated(PAGE_LAST_UPDATED);

  const simulators = [
    {
      name: 'P2P Streaming with Single ALM-tree',
      desc: 'Simulation of a multiple channel IPTV service with hybrid IP-multicast and P2P streaming. The P2P overlay uses a single ALM tree with push-based forwarding for each channel.',
    },
    {
      name: 'P2P Streaming with Multiple ALM-trees',
      desc: 'Simulation of a multiple channel IPTV service with hybrid IP-multicast and P2P streaming. The P2P overlay uses more than one ALM tree with push-based forwarding for each channel.',
    },
    {
      name: 'P2P Streaming with Mesh',
      desc: 'Simulation of a multiple channel IPTV service with hybrid IP-multicast and P2P streaming. The P2P overlay uses a mesh structure for each channel with segment-level pull-based forwarding. Segment scheduling is done according to the DoNET/Coolstreaming algorithm.',
    },
    {
      name: 'Unicast Connection Measurement',
      desc: 'Simplified simulation of a multiple channel IPTV service for the purpose of measuring the hop-based distance between hosts.',
    },
    {
      name: 'Multicast Connection Measurement',
      desc: 'Simplified simulation of a multiple channel IPTV service for the purpose of measuring the size of the IP multicast tree.',
    },
    {
      name: 'TFRC Flow Throughput Measurement',
      desc: 'Simulation of TFRC flow congestion control.',
    },
  ];

  return (
    <div className="project-page" id="top">
      <h1 className="title">SimStream</h1>
      <p>
        The SimStream simulation software for video streaming is a project in progress and the software on this page is
        subject to change. There are no guarantees for this software and you can use it on your own risk. SimStream is
        free software and is licensed under the terms of{' '}
        <a href="http://www.gnu.org/licenses/gpl.html" target="_blank" rel="noopener noreferrer">
          GNU General Public License
        </a>
        .
      </p>
      <h1>Online Resources</h1>
      <ul>
        <li>
          <a href="/projects/simstream/code/html/index.html">Source code documentation</a>
        </li>
        <li>
          Download the open source code as <a href="/download/SimStream.zip">ZIP (305 KB)</a> or{' '}
          <a href="/download/SimStream.tgz">TAR (207 KB)</a> archive (version 2010.12.14)
        </li>
      </ul>

      <h1 id="contents">On This Page</h1>
      <ol type="I">
        <li>
          <a href="#s1">The Basics</a>
          <ol>
            <li><a href="#s11">What is SimStream?</a></li>
            <li><a href="#s12">Architecture and Main Components</a></li>
          </ol>
        </li>
        <li>
          <a href="#s2">Simulator Core</a>
          <ol>
            <li><a href="#s21">The Run-Time</a></li>
            <li><a href="#s22">Objects and Cross-Communication</a></li>
            <li><a href="#s23">Timers</a></li>
          </ol>
        </li>
        <li><a href="#s3">Networking Library</a></li>
        <li><a href="#s4">Streaming Library</a></li>
        <li><a href="#s5">Simulation of Large-Scale P2P Streaming</a></li>
      </ol>

      <h1 id="s1">I. The Basics</h1>
      <p>In this chapter you will learn the basics of the SimStream simulation software:</p>
      <ul>
        <li>What is SimStream and what are the main features?</li>
        <li>What is the software architecture and the basic components?</li>
        <li>How can you use the basic components to create a simple simulator?</li>
      </ul>
      <NavLinks />
      <h2 id="s11">I.1. What is SimStream?</h2>
      <p>
        The <em>streaming simulator</em>, or <em>SimStream</em> for short, is a packet-level time-discrete network
        simulator, designed to be efficient for simulating large scale streaming applications. A particular requirement
        of these applications is that they need to simulate a large number of hosts and packets leading to extensive
        simulation times for many scenarios.
      </p>
      <p>Compared with existing network simulators, SimStream reduces the complexity and computing times with the following key aspects:</p>
      <ul>
        <li>Uses native executable code written in C++</li>
        <li>Uses index-based addressing making packet routing and forwarding a constant-time operation</li>
        <li>Uses delegate-based communication between simulator components</li>
      </ul>
      <NavLinks />
      <h2 id="s12">I.2. Architecture and Main Components</h2>
      <p>SimStream consists of the following main components:</p>
      <ul>
        <li>The core</li>
        <li>A set of add-on libraries</li>
        <li>A set of implemented simulators</li>
      </ul>
      <p>
        The <a href="#fig1a">figure I.a</a> illustrates the main components and sub-components.
      </p>
      <p>
        <img src="/projects/simstream/Fig1a.png" width={683} height={456} alt="Figure I.a" id="fig1a" />
      </p>
      <p className="figure">Figure I.a. SimStream architecture and simulators currently implemented</p>
      <h4>The simulator core</h4>
      <p>
        The <a href="#s2">simulator core</a> is the central part of the simulator that can be used to create any type of
        time-discrete simulations, not necessarily computer network simulations. The core uses an event scheduling
        mechanism to serialize the execution of tasks according to their temporal order within the simulation.
      </p>
      <h4>The simulation libraries</h4>
      <p>
        The simulation libraries bring an extensive set of functionality that is required to implement network
        simulations for multimedia streaming. Although the libraries are not at all essential when developing a fully
        customized simulation from scratch, they provide common used elements such as basic implementation of several
        network protocols, network entities such as hosts, routers and links, streaming client and server application
        layers, streaming encoders and decoders, etc.
      </p>
      <h4>The simulators</h4>
      <p>
        The simulators is a list of customized simulator implementation that are part of the current version of
        SimStream. The following table illustrates the current simulators and their purpose.
      </p>
      <table className="information" style={{ width: '800px' }}>
        <tbody>
          <tr>
            <th className="strong" style={{ width: '250px' }}>Simulator</th>
            <th style={{ width: '550px' }}>Objective</th>
          </tr>
          {simulators.map((s) => (
            <tr key={s.name}>
              <td className="strong">{s.name}</td>
              <td>{s.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <NavLinks />

      <h1 id="s2">II. Simulator Core</h1>
      <p>This chapter describes the core of the SimStream simulator:</p>
      <ul>
        <li>The run-time and the scheduling of simulation of events, calls</li>
        <li>The role of the simulation model</li>
        <li>Simulation objects and cross-communication between them</li>
        <li>Timers and their usage</li>
      </ul>
      <NavLinks />
      <h2 id="s21">II.1. The Run-Time</h2>
      <p>
        The run-time is the central part of the simulator. It manages the execution flow of the simulation tasks in the
        form of <em>simulation events</em> and <em>simulation calls</em>. In addition, it keeps track of the simulation{' '}
        <em>virtual time</em>, and provides the methods for the scheduling and cancellation of events and calls.
      </p>
      <table className="information" style={{ width: '500px' }}>
        <tbody>
          <tr>
            <th className="strong" style={{ width: '150px' }}>Class</th>
            <th>Description</th>
          </tr>
          <tr>
            <td>
              <a href="/projects/simstream/code/html/class_c_sim.html">CSim</a>
            </td>
            <td>The simulator run-time.</td>
          </tr>
          <tr>
            <td>
              <a href="/projects/simstream/code/html/class_c_sim_handler.html">CSimHandler</a>
            </td>
            <td>The handler to the simulator run-time.</td>
          </tr>
        </tbody>
      </table>
      <NavLinks />

      <h1 id="s3">III. Networking Library</h1>
      <div className="box">Coming soon!</div>
      <NavLinks />

      <h1 id="s4">IV. Streaming Library</h1>
      <div className="box">Coming soon!</div>
      <NavLinks />

      <h1 id="s5">V. Simulation of Large-Scale P2P Streaming</h1>
      <div className="box">Coming soon!</div>
      <NavLinks />

      <p className="update">Last updated: December 29, 2010</p>
    </div>
  );
};

export default SimStreamPage;
